import { ref } from 'vue'
import { useEventListener } from '@vueuse/core'

/**
 * The "a drag is over this drop zone" flag, without the child-boundary flicker (MAR-1473).
 *
 * `dragleave` fires on every element the pointer leaves and bubbles, so a zone that clears its flag
 * on each bubbled `dragleave` switches off whenever the drag crosses one of its OWN descendants —
 * the overlay unmounted and faded back in on the next `dragover`. Here a leave counts only when the
 * pointer went somewhere outside the zone: `relatedTarget` outside `currentTarget`, or null (the
 * pointer left the window).
 *
 * Bind `onDragLeave` as the zone's `@dragleave` and set `active` from its own `@dragover`. The flag
 * also resets on ANY drop or dragend in the window (capture phase; the string overload binds to the
 * window on the client only, so this is SSR-safe), because once a leave between
 * children no longer clears it, a drop that a child handles and stops from bubbling — or a drag
 * cancelled with Escape — would otherwise leave it stuck on.
 */
export function useDragHighlight() {
  const active = ref(false)

  function onDragLeave(e: DragEvent) {
    const zone = e.currentTarget as Node | null
    const to = e.relatedTarget as Node | null
    if (zone && to && zone.contains(to)) return
    active.value = false
  }

  function reset() {
    active.value = false
  }

  useEventListener('drop', reset, { capture: true })
  useEventListener('dragend', reset, { capture: true })

  return { active, onDragLeave, reset }
}
